import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from "@chakra-ui/react";
import { FaPlus, FaTrash } from "react-icons/fa";
import axios from "axios";
import { API_URL } from "../config";
import { getToken } from "../token";
import { FORUM, TRANSITION } from "../Theme/styles";

const STATUSES = [
  { key: "planned", name: "Planowane" },
  { key: "in_progress", name: "W trakcie" },
  { key: "done", name: "Zakończone" },
];

const StageEditor = ({ stages, settings, onClose, onChange }) => {
  const [name, setName] = useState("");
  const [release, setRelease] = useState(settings?.release_label || "");
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const headers = { Authorization: `Bearer ${getToken()}` };
  const config = { withCredentials: true, headers };

  useEffect(() => {
    axios
      .get(`${API_URL}/tasks`, config)
      .then((res) => setTasks(res.data || []))
      .catch(() => setTasks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = (promise) => {
    setBusy(true);
    setError(null);
    return promise
      .then(() => onChange())
      .catch((e) => setError(e?.response?.data?.error || "Nie udało się zapisać"))
      .finally(() => setBusy(false));
  };

  const addStage = () => {
    if (!name.trim()) return;
    run(
      axios.post(
        `${API_URL}/roadmap/stages`,
        { name: name.trim(), position: stages.length },
        config
      )
    ).then(() => setName(""));
  };

  const setStatus = (stage, status) =>
    run(axios.put(`${API_URL}/roadmap/stages/${stage.id}`, { status }, config));

  const removeStage = (stage) =>
    run(axios.delete(`${API_URL}/roadmap/stages/${stage.id}`, config));

  const saveRelease = () =>
    run(axios.put(`${API_URL}/roadmap/settings`, { release_label: release.trim() || null }, config));

  return (
    <Modal isOpen onClose={onClose} size={{ base: "full", md: "2xl" }} scrollBehavior={"inside"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Roadmapa — układ</ModalHeader>
        <ModalCloseButton mt={2} />

        <ModalBody>
          <Flex direction={"column"} gap={5}>
            <Box>
              <Text fontSize={"sm"} fontWeight={"600"} color={FORUM.tekstJasny} mb={2}>
                Planowana premiera
              </Text>
              <Flex gap={2}>
                <Input
                  size={"sm"}
                  placeholder={"np. Grudzień 2026"}
                  value={release}
                  onChange={(e) => setRelease(e.target.value)}
                />
                <Button size={"sm"} variant={"drugorzedny"} onClick={saveRelease} isDisabled={busy}>
                  Zapisz
                </Button>
              </Flex>
            </Box>

            <Box>
              <Text fontSize={"sm"} fontWeight={"600"} color={FORUM.tekstJasny} mb={2}>
                Etapy
              </Text>

              <Flex direction={"column"} gap={2} mb={3}>
                {stages.map((stage) => (
                  <Flex
                    key={stage.id}
                    align={"center"}
                    gap={2}
                    border={"1px solid"}
                    borderColor={FORUM.obramowanie}
                    borderRadius={"md"}
                    px={3}
                    py={2}
                  >
                    <Text fontSize={"sm"} color={FORUM.tekstJasny} flex={1} noOfLines={1}>
                      {stage.name}
                    </Text>
                    <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony} whiteSpace={"nowrap"}>
                      {stage.items.length} poz.
                    </Text>
                    <Select
                      size={"xs"}
                      maxW={"130px"}
                      value={stage.status}
                      onChange={(e) => setStatus(stage, e.target.value)}
                    >
                      {STATUSES.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                    <Box
                      as={"button"}
                      color={FORUM.tekstPrzygaszony}
                      transition={TRANSITION}
                      _hover={{ color: "#E06C6C" }}
                      onClick={() => removeStage(stage)}
                      title={"Usuń etap razem z pozycjami"}
                    >
                      <FaTrash size={11} />
                    </Box>
                  </Flex>
                ))}
              </Flex>

              <Flex gap={2}>
                <Input
                  size={"sm"}
                  placeholder={"Nazwa nowego etapu"}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addStage()}
                />
                <Button size={"sm"} variant={"glowny"} onClick={addStage} isDisabled={busy}>
                  Dodaj
                </Button>
              </Flex>
            </Box>

            {stages.map((stage) => (
              <StageItems
                key={stage.id}
                stage={stage}
                tasks={tasks}
                config={config}
                onChange={onChange}
                setError={setError}
              />
            ))}

            {error && (
              <Text fontSize={"xs"} color={"#F08A80"}>
                {error}
              </Text>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant={"drugorzedny"} onClick={onClose}>
            Zamknij
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const StageItems = ({ stage, tasks, config, onChange, setError }) => {
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [taskId, setTaskId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const add = () => {
    if (!taskId && !label.trim()) return;
    axios
      .post(
        `${API_URL}/roadmap/items`,
        {
          stage_id: stage.id,
          task_id: taskId || null,
          label: taskId ? null : label.trim(),
          note: note.trim() || null,
          start_date: from || null,
          end_date: to || null,
          position: stage.items.length,
        },
        config
      )
      .then(() => {
        setLabel("");
        setNote("");
        setTaskId("");
        setFrom("");
        setTo("");
        onChange();
      })
      .catch((e) => setError(e?.response?.data?.error || "Nie udało się dodać pozycji"));
  };

  const toggle = (item) =>
    axios
      .put(`${API_URL}/roadmap/items/${item.id}`, { done: !item.done }, config)
      .then(onChange)
      .catch((e) => setError(e?.response?.data?.error || "Nie udało się zmienić"));

  const remove = (item) =>
    axios
      .delete(`${API_URL}/roadmap/items/${item.id}`, config)
      .then(onChange)
      .catch(() => setError("Nie udało się usunąć"));

  return (
    <Box border={"1px solid"} borderColor={FORUM.obramowanie} borderRadius={"md"} p={3}>
      <Text fontSize={"sm"} fontWeight={"600"} color={FORUM.tekstJasny} mb={2}>
        {stage.name}
      </Text>

      <Flex direction={"column"} gap={1.5} mb={3}>
        {stage.items.map((item) => (
          <Flex key={item.id} align={"center"} gap={2}>
            <Box
              as={"button"}
              fontSize={"11px"}
              color={item.done ? "#4FB477" : FORUM.tekstPrzygaszony}
              onClick={() => !item.task_id && toggle(item)}
              cursor={item.task_id ? "default" : "pointer"}
              title={
                item.task_id
                  ? "Odhacza się razem z zadaniem"
                  : item.done
                  ? "Cofnij"
                  : "Oznacz jako zrobione"
              }
            >
              {item.done ? "✔" : "○"}
            </Box>
            <Text fontSize={"sm"} color={FORUM.tekst} flex={1} noOfLines={1}>
              {item.label}
              {item.task_id && (
                <Text as={"span"} fontSize={"10px"} color={"#6C99FF"} ml={2}>
                  zadanie
                </Text>
              )}
            </Text>
            <Box
              as={"button"}
              color={FORUM.tekstPrzygaszony}
              transition={TRANSITION}
              _hover={{ color: "#E06C6C" }}
              onClick={() => remove(item)}
            >
              <FaTrash size={10} />
            </Box>
          </Flex>
        ))}
      </Flex>

      <Flex gap={2} wrap={"wrap"} align={"center"}>
        <Select
          size={"xs"}
          maxW={"220px"}
          value={taskId}
          onChange={(e) => setTaskId(e.target.value)}
        >
          <option value={""}>— wpis własny —</option>
          {tasks.map((t) => (
            <option key={t.id} value={t.id}>
              {t.title}
            </option>
          ))}
        </Select>

        {!taskId && (
          <>
            <Input
              size={"xs"}
              maxW={"180px"}
              placeholder={"Nazwa pozycji"}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <Input
              size={"xs"}
              maxW={"180px"}
              placeholder={"Opis (opcjonalny)"}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Input
              size={"xs"}
              maxW={"130px"}
              type={"date"}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              title={"Od (opcjonalne)"}
            />
            <Input
              size={"xs"}
              maxW={"130px"}
              type={"date"}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              title={"Do (opcjonalne)"}
            />
          </>
        )}

        <Flex
          as={"button"}
          align={"center"}
          gap={1.5}
          color={FORUM.tekstPrzygaszony}
          transition={TRANSITION}
          _hover={{ color: FORUM.tekstJasny }}
          onClick={add}
        >
          <FaPlus size={10} />
          <Text fontSize={"xs"}>Dodaj pozycję</Text>
        </Flex>
      </Flex>
    </Box>
  );
};

export default StageEditor;
